import React from 'react';
import { Chip } from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Error as ErrorIcon,
    InfoOutlined as InfoIcon,
} from '@mui/icons-material';

// ---------------------------------------------------------------------------
// Shared formatting + acceptance helpers for the MSA module result renderers.
//
// Engines emit different acceptance-band vocabularies:
//   type3 GRR                              : acceptable | conditional | unacceptable | undetermined
//   attribute / attributeAnalytic /        : acceptable | marginal    | unacceptable
//   capabilityImpact (server-derived)
// Both 'conditional' and 'marginal' are the middle band → amber.
// ---------------------------------------------------------------------------

export function acceptanceColor(status) {
    switch (status) {
        case 'acceptable':   return '#81c784';
        case 'conditional':
        case 'marginal':     return '#ffb74d';
        case 'unacceptable': return '#e57373';
        default:             return '#90caf9';
    }
}

const ACCEPTANCE_LABEL = {
    acceptable:   'Acceptable',
    conditional:  'Conditional',
    marginal:     'Marginal',
    unacceptable: 'Unacceptable',
    undetermined: 'Undetermined',
};

const ACCEPTANCE_ICON = {
    acceptable:   CheckCircleIcon,
    conditional:  WarningIcon,
    marginal:     WarningIcon,
    unacceptable: ErrorIcon,
};

/**
 * Acceptance status chip, shared across every MSA result renderer.
 * @param {{ status?: string, label?: string, size?: 'small'|'medium' }} props
 */
export function AcceptanceBadge({ status, label, size = 'medium' }) {
    if (!status) {
        return <Chip label={label ? `${label}: —` : '—'} size="small" sx={{ color: 'rgba(255,255,255,0.4)' }} />;
    }
    const color = acceptanceColor(status);
    const Icon  = ACCEPTANCE_ICON[status] || InfoIcon;
    const text  = ACCEPTANCE_LABEL[status] || status;
    return (
        <Chip
            icon={<Icon sx={{ fontSize: '1rem !important', color: `${color} !important` }} />}
            label={label ? `${label}: ${text}` : text}
            size={size}
            sx={{
                backgroundColor: `${color}18`,
                border: `1px solid ${color}55`,
                color,
                fontWeight: 600,
                fontSize: size === 'small' ? '0.72rem' : '0.8rem',
            }}
        />
    );
}

// ---------------------------------------------------------------------------
// Number formatting — all null / NaN / Infinity safe (→ em dash).
// ---------------------------------------------------------------------------

/** Fixed-decimal number; '—' when null/undefined/non-finite. */
export function fmt(v, decimals = 4) {
    if (v === null || v === undefined || !isFinite(v)) return '—';
    return Number(v).toFixed(decimals);
}

/** `v` is ALREADY a percentage number (e.g. 12.5 → "12.5%"). */
export function fmtPct(v, decimals = 1) {
    if (v === null || v === undefined || !isFinite(v)) return '—';
    return `${Number(v).toFixed(decimals)}%`;
}

/** `v` is a FRACTION in [0,1] (e.g. 0.125 → "12.5%"). */
export function fmtFrac(v, decimals = 1) {
    if (v === null || v === undefined || !isFinite(v)) return '—';
    return `${(Number(v) * 100).toFixed(decimals)}%`;
}

/** Signed fixed-decimal (e.g. +0.312 / -0.020); for deltas like Cpk improvement. */
export function fmtSigned(v, decimals = 3) {
    if (v === null || v === undefined || !isFinite(v)) return '—';
    const s = Number(v).toFixed(decimals);
    return Number(v) > 0 ? `+${s}` : s;
}

const msaFormat = { acceptanceColor, AcceptanceBadge, fmt, fmtPct, fmtFrac, fmtSigned };
export default msaFormat;
